/**
 * SparesTab — the "Spares" sub-tab of the Finance page.
 *
 * 2026-07-11 (Batch-B G1) — this is the ORIGINAL FinancePage.jsx content
 * (Apr 2026, Tom_Notes E2), moved here UNCHANGED apart from:
 *   • `teamId` now arrives as a prop from the FinancePage shell instead of
 *     useParams() (the shell already resolved it and gates coach-only
 *     access + the payment_enabled visibility check before this ever
 *     mounts).
 *   • The page-level header ("Finance" h1 + coach-only guard) moved up to
 *     FinancePage.jsx, which now owns the Overview / Spares / Full Timers
 *     / Expenses sub-nav.
 *   • Import paths adjusted for the new frontend/src/pages/finance/ folder.
 * Its own INNER tab switcher (Overview / Outstanding / By Game / By Player)
 * and all three modals (CashPaymentModal, OutstandingPlayerModal,
 * PlayerTransactionsModal) are untouched.
 *
 * Backend: GET  /api/payments/team/:teamId/finance
 *          GET  /api/payments/team/:teamId/finance/export.csv
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Users, Calendar as CalendarIcon,
  TrendingUp, AlertTriangle, CheckCircle,
} from 'lucide-react';
import api from '../../services/api.js';
import toast from 'react-hot-toast';
import { PageSpinner } from '../../components/Skeleton.jsx';
import { format, parseISO } from 'date-fns';
import ExportMenu from '../../components/ExportMenu.jsx';

// Currency formatter — same approach as ActivityPage. We intentionally
// don't use Intl with a 'symbol' display option because Stripe + the
// rest of the app round to 2dp regardless of currency, and this gives
// us full control of the layout (the lucide DollarSign icon does
// double duty as a visual cue alongside the numeric value).
const fmtMoney = (cents, currency = 'cad') => {
  const amount = (cents || 0) / 100;
  const ccy = (currency || 'cad').toUpperCase();
  return `${ccy} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// 24h DB time string ("21:00:00" / "21:30") -> friendly 12h ("9 PM" / "9:30 PM").
const fmtTime12 = (t) => {
  if (!t) return '';
  const [hStr, mStr] = String(t).split(':');
  let h = parseInt(hStr, 10);
  if (!Number.isFinite(h)) return '';
  const m = parseInt(mStr, 10) || 0;
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12; if (h === 0) h = 12;
  return m ? `${h}:${String(m).padStart(2, '0')} ${ampm}` : `${h} ${ampm}`;
};

export default function SparesTab({ teamId }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  // Tabs: overview | byGame | byPlayer. Default overview because it
  // gives the at-a-glance numbers a coach is looking for first.
  const [tab,     setTab]     = useState('overview');
  // v2.5.153 — drill-down modal state. Holds the userId currently
  // being viewed, the resolved name (for the modal header), and the
  // fetched payment history. Clicking a player row in the By Player
  // table opens this; backdrop click or X closes it.
  const [drillUser,     setDrillUser]     = useState(null); // { userId, name } or null
  const [drillData,     setDrillData]     = useState(null);
  const [drillLoading,  setDrillLoading]  = useState(false);

  // 2026-07-11 (batch-B G3) — Stripe Connect status line under the page
  // header. Reuses the SAME coach-gated status endpoint TeamPage.jsx's
  // Payments tab already calls (GET /teams/:teamId/stripe-connect/status,
  // backend/src/routes/stripe-connect.js) rather than inventing a second
  // source of truth. null = still loading / fetch failed — the status
  // line just doesn't render rather than showing a misleading default.
  const [stripeStatus, setStripeStatus] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/teams/${teamId}/stripe-connect/status`)
      .then(r => { if (!cancelled) setStripeStatus(r.data); })
      .catch(() => { if (!cancelled) setStripeStatus(null); });
    return () => { cancelled = true; };
  }, [teamId]);

  // 2026-07-11 (batch-B G4) — which player's cash-payment modal is open,
  // if any. Separate from `drillUser` (Outstanding / transactions modals)
  // so opening the cash modal never disturbs a currently-open drill-down,
  // and vice versa.
  const [cashUser, setCashUser] = useState(null); // { userId, name } or null

  // Fetch the drill-down whenever a user is selected. Cancellation
  // guards against a fast double-click selecting two different users.
  useEffect(() => {
    if (!drillUser) { setDrillData(null); return; }
    let cancelled = false;
    setDrillLoading(true);
    setDrillData(null);
    api.get(`/payments/team/${teamId}/finance/player/${drillUser.userId}`)
      .then(r => { if (!cancelled) setDrillData(r.data); })
      .catch(err => { if (!cancelled) toast.error(err.response?.data?.error || 'Failed to load player payments'); })
      .finally(() => { if (!cancelled) setDrillLoading(false); });
    return () => { cancelled = true; };
  }, [drillUser, teamId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get(`/payments/team/${teamId}/finance`);
        if (!cancelled) setData(d);
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.error || 'Failed to load finance data');
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [teamId]);

  // 2026-07-11 (batch-B G4) — silent re-fetch used after a cash payment is
  // recorded. Doesn't flip `loading` (would flash the whole page to a
  // spinner while a modal is still open) — just swaps `data` once the
  // fresh summary lands so byPlayer/byGame reflect the new cash row.
  const loadFinance = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/payments/team/${teamId}/finance`);
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to refresh finance data');
    }
  }, [teamId]);

  // v2.5.153 — the legacy server-side CSV download (downloadCsv +
  // exporting state) was removed when the toolbar moved to the
  // shared ExportMenu. ExportMenu builds CSV/Excel/PDF client-side
  // from the already-loaded data, which is faster and consistent
  // with the other tables. The backend endpoint
  //   GET /payments/team/:teamId/finance/export.csv
  // still exists for direct-URL or programmatic access — we just
  // don't expose a button for it anymore.

  if (loading) return <PageSpinner />;
  if (!data) return null;

  const { currency, spareFeeCents, goaliesFree, totals, byGame, byPlayer, cashTotalCents = 0 } = data;
  // Pick the primary currency for the headline tiles. Falls back to
  // the team's default currency if the totals map is empty.
  const primaryCcy = Object.keys(totals)[0] || currency;
  const t = totals[primaryCcy] || { collectedCents: 0, pendingCents: 0, refundedCents: 0, count: { paid: 0, pending: 0, refunded: 0 } };

  const totalPaidPlayers = byPlayer.filter(p => p.netPaidCents > 0).length;
  // Players who still owe a spare fee, biggest balance first — the
  // core of the Outstanding tab.
  const outstandingPlayers = byPlayer
    .filter(p => p.owedCents > 0)
    .sort((a, b) => b.owedCents - a.owedCents);
  const playersWithDebt  = outstandingPlayers.length;
  // Banner total + game count are derived from the SAME per-player data
  // as the list, so the headline always reconciles with the table.
  // (The by-game aggregate could over-count vs by-player in edge cases.)
  const outstandingTotal = outstandingPlayers.reduce((s, p) => s + (p.owedCents || 0), 0);
  const gamesWithDebt    = outstandingPlayers.reduce((s, p) => s + (p.unpaidGames || 0), 0);
  const hasOutstanding   = playersWithDebt > 0;

  return (
    <div>
      <p className="text-secondary text-sm mb-3" style={{ lineHeight: 1.5 }}>
        Spare-fee collection at a glance. Outstanding amounts are based on
        confirmed spare check-ins where no successful payment exists in
        the app — players who paid offline (e-transfer, cash) won't
        clear here unless you record the payment manually.
      </p>

      {/* ── G3: Stripe Connect status line — FYI only, no action
          buttons here (that's Team Settings → Payments). Renders
          nothing while stripeStatus is still loading/unknown so we
          never show a false negative for a beat. */}
      {stripeStatus && (
        <div className="text-sm mb-5 flex items-start gap-2" style={{
          color: (stripeStatus.connected && stripeStatus.chargesEnabled) ? 'var(--goal)' : 'var(--puck)',
        }}>
          {(stripeStatus.connected && stripeStatus.chargesEnabled)
            ? <CheckCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
            : <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />}
          <span>
            {stripeStatus.connected && stripeStatus.chargesEnabled
              ? '✓ Stripe account connected — card payments active.'
              : stripeStatus.connected
                // Connected but onboarding not finished — card payments won't
                // actually go through yet even though an account exists.
                ? '⚠ Stripe account connected but onboarding isn\'t finished yet — card payments aren\'t active. Record payments as cash until it\'s complete.'
                : '⚠ No Stripe account connected — record payments as cash or use Free Play.'}
            {' '}
            <span style={{ color: 'var(--text-3)' }}>Team Settings → Payments</span>
          </span>
        </div>
      )}

      {/* ── Tab switcher ── */}
      <div className="flex gap-1 mb-5" style={{
        background: 'var(--surface-2)', padding: 4, borderRadius: 12,
      }}>
        {[
          { key: 'overview',    label: 'Overview',    icon: TrendingUp },
          { key: 'outstanding', label: 'Outstanding', icon: AlertTriangle },
          { key: 'byGame',      label: 'By Game',     icon: CalendarIcon },
          { key: 'byPlayer',    label: 'By Player',   icon: Users },
        ].map(opt => {
          const active = tab === opt.key;
          const Icon = opt.icon;
          // The Outstanding tab carries a live count badge so a coach
          // sees "money is owed" without opening it.
          const showBadge = opt.key === 'outstanding' && playersWithDebt > 0;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => setTab(opt.key)}
              className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-bold transition-all"
              style={{
                background: active ? 'var(--surface)' : 'transparent',
                color: active ? 'var(--ice)' : 'var(--text-2)',
                boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}>
              <Icon size={14} style={showBadge ? { color: 'var(--penalty)' } : undefined} />
              <span>{opt.label}</span>
              {showBadge && (
                <span style={{
                  minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9,
                  background: 'var(--penalty)', color: '#fff', fontSize: 11, fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {playersWithDebt}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === 'overview' && (
        <>
          {/* Top tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {/* 2026-09-17 (per Gilles) — "Collected" is the NET amount that
                reached the coach: card payments minus refunds minus system
                fees (Game Day platform fee + Stripe's processing cut), plus
                cash recorded by the coach. Gross and fees shown underneath. */}
            <Tile
              label="Collected (net to you)"
              value={fmtMoney((t.netToCoachCents != null ? t.netToCoachCents : (t.collectedCents - (t.refundedCents || 0))) , primaryCcy)}
              sub={`${fmtMoney(t.collectedCents, primaryCcy)} card${cashTotalCents ? ` + ${fmtMoney(cashTotalCents, primaryCcy)} cash` : ''} − ${fmtMoney(t.systemFeeCents || 0, primaryCcy)} system fees${t.feesEstimated ? ' (est.)' : ''}`}
              tone="goal"
            />
            <Tile
              label="Pending"
              value={fmtMoney(t.pendingCents, primaryCcy)}
              sub={`${t.count?.pending || 0} pending`}
              tone="puck"
            />
            <Tile
              label="Refunded"
              value={fmtMoney(t.refundedCents, primaryCcy)}
              sub={`${t.count?.refunded || 0} refunds`}
              tone="muted"
            />
            <Tile
              label="Estimated outstanding"
              value={fmtMoney(outstandingTotal, primaryCcy)}
              sub={playersWithDebt > 0 ? `${playersWithDebt} player${playersWithDebt === 1 ? '' : 's'}` : 'Everyone\'s square'}
              tone={outstandingTotal > 0 ? 'penalty' : 'goal'}
            />
          </div>

          {/* Setup snapshot */}
          <div className="card mb-5">
            <h3 className="font-semibold text-sm mb-3">Setup</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <KV k="Default currency" v={(currency || 'cad').toUpperCase()} />
              <KV k="Spare fee" v={fmtMoney(spareFeeCents, currency)} />
              <KV k="Goalies free" v={goaliesFree ? 'Yes' : 'No'} />
            </div>
          </div>

          {/* Multi-currency notice — only when more than 1 currency in totals */}
          {Object.keys(totals).length > 1 && (
            <div className="card mb-5" style={{
              background: 'rgba(255,149,0,0.06)',
              border: '1px solid rgba(255,149,0,0.18)',
            }}>
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} style={{ color: 'var(--puck)', flexShrink: 0, marginTop: 2 }} />
                <div className="text-sm" style={{ lineHeight: 1.5 }}>
                  <strong>Multiple currencies detected.</strong> Totals are tracked per currency
                  separately so the math adds up cleanly even after a mid-season currency change.
                  Switch to <em>By Game</em> or <em>By Player</em> for the breakdown.
                </div>
              </div>
            </div>
          )}

          {/* Quick stats row */}
          <div className="card">
            <h3 className="font-semibold text-sm mb-3">At a glance</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <KV k="Players who've paid"  v={`${totalPaidPlayers}`} />
              <KV k="Players with balance" v={`${playersWithDebt}`} />
              <KV k="Games tracked"        v={`${byGame.length}`} />
            </div>
          </div>
        </>
      )}

      {/* ── OUTSTANDING (spare payments owed) ── */}
      {tab === 'outstanding' && (
        <>
          {/* Status indicator — the headline answer to "does anyone owe?" */}
          {hasOutstanding ? (
            <div className="card mb-5" style={{
              background: 'rgba(255,68,68,0.06)', border: '1px solid rgba(255,68,68,0.22)',
            }}>
              <div className="flex items-start gap-3">
                <AlertTriangle size={20} style={{ color: 'var(--penalty)', flexShrink: 0, marginTop: 2 }} />
                <div>
                  <div className="font-bold text-base" style={{ color: 'var(--penalty)' }}>
                    {fmtMoney(outstandingTotal, primaryCcy)} outstanding
                  </div>
                  <div className="text-sm" style={{ color: 'var(--text-2)', marginTop: 2, lineHeight: 1.5 }}>
                    {playersWithDebt} player{playersWithDebt === 1 ? '' : 's'} still owe
                    {' '}across {gamesWithDebt} game{gamesWithDebt === 1 ? '' : 's'}.
                    Players who paid offline (e-transfer, cash) won't clear until you record the payment.
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card mb-5" style={{
              background: 'rgba(0,232,122,0.06)', border: '1px solid rgba(0,232,122,0.2)',
            }}>
              <div className="flex items-center gap-3">
                <CheckCircle size={20} style={{ color: 'var(--goal)', flexShrink: 0 }} />
                <div>
                  <div className="font-bold text-base" style={{ color: 'var(--goal)' }}>
                    All settled
                  </div>
                  <div className="text-sm" style={{ color: 'var(--text-2)', marginTop: 2 }}>
                    No outstanding spare payments right now.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Who owes — only players with a positive balance */}
          <div className="card">
            <div className="mb-3">
              <h3 className="font-semibold text-sm">Players with outstanding spare fees</h3>
            </div>
            {outstandingPlayers.length === 0 ? (
              <p className="text-secondary text-sm py-4 text-center">
                Nobody owes a spare fee right now. 🎉
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="w-full text-sm" style={{ minWidth: 480 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <th className="text-left py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}>Player</th>
                      <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}>Unpaid Games</th>
                      <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}>Owes</th>
                      <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {outstandingPlayers.map(p => (
                      <tr
                        key={p.userId}
                        onClick={() => setDrillUser({ userId: p.userId, name: p.name, mode: 'outstanding' })}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setDrillUser({ userId: p.userId, name: p.name, mode: 'outstanding' }); }}
                        style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background 0.1s' }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-2)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                        <td className="py-2 px-2">
                          <div className="font-medium" style={{ color: 'var(--ice)' }}>{p.name}</div>
                          <div className="text-xs" style={{ color: 'var(--text-3)' }}>{p.email}</div>
                        </td>
                        <td className="py-2 px-2 text-right">{p.unpaidGames}</td>
                        <td className="py-2 px-2 text-right" style={{ color: 'var(--penalty)', fontWeight: 700 }}>
                          {fmtMoney(p.owedCents, currency)}
                        </td>
                        <td className="py-2 px-2 text-right">
                          {/* G4 — record an out-of-system (cash) payment. Stop
                              propagation so this doesn't also open the row's
                              outstanding-games drill-down modal. */}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setCashUser({ userId: p.userId, name: p.name }); }}
                            className="btn-ice text-xs px-3 py-1"
                            style={{ whiteSpace: 'nowrap' }}>
                            💵 Cash
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-secondary mt-2 px-2">
                  💡 Click a player to see the games they owe for and send a payment link, or use 💵 Cash to record an
                  in-person payment.
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── BY GAME ── */}
      {tab === 'byGame' && (
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm">Per-game breakdown</h3>
            {/* v2.5.153 — multi-format export (CSV / Excel / PDF).
                Builds rows from the already-loaded `byGame` array so
                what's exported matches what's on screen. */}
            <ExportMenu
              filename="finance-by-game"
              title="Finance — Per-game breakdown"
              columns={[
                { key: 'date',        label: 'Date' },
                { key: 'arena',       label: 'Arena' },
                { key: 'spares',      label: 'Spares',      align: 'right' },
                { key: 'expected',    label: 'Expected',    align: 'right' },
                { key: 'gross',       label: 'Collected (card)', align: 'right' },
                { key: 'fees',        label: 'System fees', align: 'right' },
                { key: 'cash',        label: 'Cash',        align: 'right' },
                { key: 'net',         label: 'Net to coach', align: 'right' },
                { key: 'outstanding', label: 'Outstanding', align: 'right' },
              ]}
              getRows={() => byGame.map(g => {
                let dateStr = '';
                try {
                  if (g.gameDate) dateStr = format(parseISO(String(g.gameDate).slice(0, 10)), 'yyyy-MM-dd');
                } catch {}
                return {
                  date:        dateStr,
                  arena:       g.arena || '',
                  spares:      g.spareCount,
                  expected:    fmtMoney(g.expectedCents,    currency),
                  gross:       fmtMoney(g.grossCardCents ?? (g.collectedCents - (g.cashCents || 0)), currency),
                  fees:        fmtMoney(g.systemFeeCents || 0, currency),
                  cash:        g.cashCents > 0 ? fmtMoney(g.cashCents, currency) : '',
                  net:         fmtMoney(g.netToCoachCents ?? g.netCollectedCents, currency),
                  outstanding: fmtMoney(g.outstandingCents,  currency),
                };
              })}
            />
          </div>
          {byGame.length === 0 ? (
            <p className="text-secondary text-sm py-4 text-center">
              No games with payment activity yet.
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="w-full text-sm" style={{ minWidth: 760 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th className="text-left py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}>Date</th>
                    <th className="text-left py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}>Arena</th>
                    <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}>Spares</th>
                    <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}>Expected</th>
                    {/* 2026-09-17 (per Gilles) — gross card / system fees / cash / net */}
                    <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }} title="Card payments received (before fees)">Collected</th>
                    <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }} title="Game Day platform fee + Stripe processing (est. 2.9% + 30¢)">System fees</th>
                    <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }} title="Cash you recorded as paid">Cash</th>
                    <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }} title="Card − refunds − fees + cash">Net to you</th>
                    <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}>Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {byGame.map(g => {
                    let dateStr = '—';
                    try {
                      if (g.gameDate) {
                        dateStr = format(parseISO(String(g.gameDate).slice(0, 10)), 'MMM d');
                      }
                    } catch {}
                    const owesColor = g.outstandingCents > 0 ? 'var(--puck)' : 'var(--text-2)';
                    return (
                      <tr key={g.gameId} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td className="py-2 px-2">{dateStr}</td>
                        <td className="py-2 px-2 text-xs" style={{ color: 'var(--text-2)' }}>
                          {g.arena || '—'}
                        </td>
                        <td className="py-2 px-2 text-right">{g.spareCount}</td>
                        <td className="py-2 px-2 text-right text-xs" style={{ color: 'var(--text-2)' }}>
                          {fmtMoney(g.expectedCents, currency)}
                        </td>
                        <td className="py-2 px-2 text-right" style={{ color: 'var(--text)' }}>
                          {fmtMoney(g.grossCardCents ?? (g.collectedCents - (g.cashCents || 0)), currency)}
                        </td>
                        <td className="py-2 px-2 text-right text-xs" style={{ color: 'var(--text-2)' }}>
                          {(g.systemFeeCents || 0) > 0 ? `− ${fmtMoney(g.systemFeeCents, currency)}` : '—'}
                        </td>
                        <td className="py-2 px-2 text-right text-xs" style={{ color: g.cashCents > 0 ? 'var(--goal)' : 'var(--text-3)' }}>
                          {g.cashCents > 0 ? fmtMoney(g.cashCents, currency) : '—'}
                        </td>
                        <td className="py-2 px-2 text-right" style={{ color: 'var(--goal)', fontWeight: 700 }}>
                          {fmtMoney(g.netToCoachCents ?? g.netCollectedCents, currency)}
                        </td>
                        <td className="py-2 px-2 text-right" style={{
                          color: owesColor, fontWeight: g.outstandingCents > 0 ? 700 : 400,
                        }}>
                          {fmtMoney(g.outstandingCents, currency)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── BY PLAYER ── */}
      {tab === 'byPlayer' && (
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm">Per-player breakdown</h3>
            <ExportMenu
              filename="finance-by-player"
              title="Finance — Per-player breakdown"
              columns={[
                { key: 'name',         label: 'Player' },
                { key: 'email',        label: 'Email' },
                { key: 'totalCollected', label: 'Total Collected', align: 'right' },
                { key: 'cash',         label: 'Cash',         align: 'right' },
                { key: 'systemFees',   label: 'System Fees',  align: 'right' },
                { key: 'netToCoach',   label: 'Net to Coach', align: 'right' },
                { key: 'refunded',     label: 'Refunded',     align: 'right' },
                { key: 'unpaidGames',  label: 'Unpaid Games', align: 'right' },
                { key: 'owes',         label: 'Owes',         align: 'right' },
              ]}
              getRows={() => byPlayer.map(p => ({
                name:        p.name,
                email:       p.email || '',
                // G4 — 3-way breakdown. systemFeeCents/netToCoachCents can be
                // genuinely unknown for legacy/platform-collect rows (no
                // per-row fee data stored); show '—' rather than a made-up 0.
                totalCollected: fmtMoney(p.totalCollectedCents, currency),
                cash:           p.cashCents > 0 ? fmtMoney(p.cashCents, currency) : '',
                systemFees:     p.feeDataMissing ? '—' : fmtMoney(p.systemFeeCents, currency),
                netToCoach:     p.feeDataMissing ? '—' : fmtMoney(p.netToCoachCents, currency),
                refunded:    p.refundedCents > 0 ? fmtMoney(p.refundedCents, currency) : '',
                unpaidGames: p.unpaidGames,
                owes:        fmtMoney(p.owedCents, currency),
              }))}
            />
          </div>
          {byPlayer.length === 0 ? (
            <p className="text-secondary text-sm py-4 text-center">
              No payment activity to report.
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="w-full text-sm" style={{ minWidth: 820 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th className="text-left py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}>Player</th>
                    {/* G4 — 3-way money breakdown replaces the old single
                        "Paid" column: Total collected (gross incl. cash),
                        System fees, Net to coach. */}
                    <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }} title="Card + cash, before fees">Total Collected</th>
                    <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }} title="Game Day platform fee + Stripe processing (est. 2.9% + 30¢)">System Fees</th>
                    <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }} title="Cash you recorded as paid (no fees)">Cash</th>
                    <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }} title="Total − refunds − fees">Net to Coach</th>
                    <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}>Refunded</th>
                    <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}>Unpaid Games</th>
                    <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}>Owes</th>
                    <th className="text-right py-2 px-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {byPlayer.map(p => (
                    <tr
                      key={p.userId}
                      onClick={() => setDrillUser({ userId: p.userId, name: p.name })}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setDrillUser({ userId: p.userId, name: p.name }); }}
                      style={{
                        borderBottom: '1px solid var(--border)',
                        cursor: 'pointer',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-2)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                      <td className="py-2 px-2">
                        {/* v2.5.153 — clickable to open drill-down modal */}
                        <div className="font-medium" style={{ color: 'var(--ice)' }}>{p.name}</div>
                        <div className="text-xs" style={{ color: 'var(--text-3)' }}>{p.email}</div>
                        {p.cashCents > 0 && (
                          <div className="text-xs" style={{ color: 'var(--text-3)' }}>
                            incl. {fmtMoney(p.cashCents, currency)} cash
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right" style={{ color: 'var(--goal)' }}>
                        {fmtMoney(p.totalCollectedCents, currency)}
                      </td>
                      {/* systemFeeCents/netToCoachCents are only DERIVABLE when
                          every paid row carries stripe_application_fee_cents
                          (cash rows always do — they're zero-fee by
                          definition). feeDataMissing flags legacy/platform-
                          collect rows with no fee data on record — we show
                          '—' rather than pretend the fee was $0. */}
                      <td className="py-2 px-2 text-right text-xs" style={{ color: 'var(--text-2)' }}>
                        {p.feeDataMissing ? '—' : ((p.systemFeeCents || 0) > 0 ? `− ${fmtMoney(p.systemFeeCents, currency)}` : '—')}
                      </td>
                      <td className="py-2 px-2 text-right text-xs" style={{ color: p.cashCents > 0 ? 'var(--goal)' : 'var(--text-3)' }}>
                        {p.cashCents > 0 ? fmtMoney(p.cashCents, currency) : '—'}
                      </td>
                      <td className="py-2 px-2 text-right" style={{ color: 'var(--goal)', fontWeight: 700 }}>
                        {p.feeDataMissing ? '—' : fmtMoney(p.netToCoachCents, currency)}
                      </td>
                      <td className="py-2 px-2 text-right text-xs" style={{ color: 'var(--text-2)' }}>
                        {p.refundedCents > 0 ? fmtMoney(p.refundedCents, currency) : '—'}
                      </td>
                      <td className="py-2 px-2 text-right">{p.unpaidGames}</td>
                      <td className="py-2 px-2 text-right" style={{
                        color: p.owedCents > 0 ? 'var(--puck)' : 'var(--text-2)',
                        fontWeight: p.owedCents > 0 ? 700 : 400,
                      }}>
                        {fmtMoney(p.owedCents, currency)}
                      </td>
                      <td className="py-2 px-2 text-right">
                        {p.owedCents > 0 && (
                          // G4 — record cash. Stop propagation so this
                          // doesn't also open the transaction-history modal.
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setCashUser({ userId: p.userId, name: p.name }); }}
                            className="btn-ice text-xs px-3 py-1"
                            style={{ whiteSpace: 'nowrap' }}>
                            💵 Cash
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-secondary mt-2 px-2">
                💡 Click a player to see their full transaction history, or use 💵 Cash to record an in-person payment.
                System fees are Game Day’s platform fee plus Stripe’s standard 2.9% + $0.30 on the card charge — the Stripe part is calculated, not read back from Stripe, so an international card can differ by a few cents. Rows that predate fee tracking show "—".
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── Drill-down modal ──
          v2.5.153 — opens when a coach taps a player row in the By
          Player table. Shows every payments row (paid, refunded,
          failed, pending, canceled) with date/game/amount/status so
          the coach has a complete picture of their activity. */}
      {drillUser && drillUser.mode === 'outstanding' && (
        <OutstandingPlayerModal
          user={drillUser}
          data={drillData}
          loading={drillLoading}
          currency={currency}
          teamId={teamId}
          onClose={() => setDrillUser(null)}
        />
      )}
      {drillUser && drillUser.mode !== 'outstanding' && (
        <PlayerTransactionsModal
          user={drillUser}
          data={drillData}
          loading={drillLoading}
          currency={currency}
          onClose={() => setDrillUser(null)}
        />
      )}

      {/* G4 — record an out-of-system (cash) payment. Independent of
          drillUser so it can be opened from either the Outstanding tab
          or the By Player tab without disturbing any other open modal. */}
      {cashUser && (
        <CashPaymentModal
          teamId={teamId}
          userId={cashUser.userId}
          name={cashUser.name}
          currency={currency}
          onClose={() => setCashUser(null)}
          onRecorded={loadFinance}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CashPaymentModal — G4 (2026-07-11). Coach records an out-of-system
// (cash) spare payment. Lists the player's OUTSTANDING games (fetched
// fresh, same endpoint as the drill-downs) with a checkbox per game and
// a per-game amount prefilled from `owedCents` — that value is already
// resolved server-side to the correct skater/goalie fee for that exact
// game (finance.js/payments.js `unpaid_spare_games` + owedCents calc),
// so we reuse it instead of re-deriving skater-vs-goalie logic here.
//
// All games start UNCHECKED — this writes a financial record, so
// nothing gets marked paid without the coach explicitly picking it.
//
// One POST per selected game (POST /payments/team/:teamId/finance/
// cash-payment, body { userId, gameId, amountCents, note }) since the
// backend endpoint is per-game. Amounts are validated client-side
// before any request fires so a bad amount on one row can't record a
// partial batch; if a request itself fails server-side (network,
// permissions) mid-batch, we still report what DID succeed and
// refresh the outstanding list rather than losing track of state.
// ─────────────────────────────────────────────────────────────
function CashPaymentModal({ teamId, userId, name, currency, onClose, onRecorded }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const [loading, setLoading]       = useState(true);
  const [games, setGames]           = useState([]); // outstandingGames from the drill endpoint
  // selection[gameId] = { checked, amount (dollar string), }
  const [selection, setSelection]   = useState({});
  const [note, setNoteText]         = useState(''); // one shared note applied to every selected game
  const [submitting, setSubmitting] = useState(false);

  // Fetch this player's outstanding games fresh every time the modal
  // opens — deliberately NOT reusing `drillData` from the page, since
  // this modal can be opened independently of any drill-down and the
  // page's drill state may be stale or for a different player.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/payments/team/${teamId}/finance/player/${userId}`)
      .then(r => {
        if (cancelled) return;
        const outstandingGames = r.data?.outstandingGames || [];
        setGames(outstandingGames);
        const initial = {};
        for (const g of outstandingGames) {
          initial[g.gameId] = { checked: false, amount: ((g.owedCents || 0) / 100).toFixed(2) };
        }
        setSelection(initial);
      })
      .catch(err => { if (!cancelled) toast.error(err.response?.data?.error || 'Failed to load outstanding games'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [teamId, userId]);

  const fmtGameDate = (v) => {
    if (!v) return '—';
    try { return format(parseISO(String(v).slice(0, 10)), 'EEE MMM d, yyyy'); } catch { return '—'; }
  };

  const toggle = (gameId) => {
    setSelection(prev => ({ ...prev, [gameId]: { ...prev[gameId], checked: !prev[gameId]?.checked } }));
  };
  const setAmount = (gameId, val) => {
    setSelection(prev => ({ ...prev, [gameId]: { ...prev[gameId], amount: val } }));
  };

  const selectedGames  = games.filter(g => selection[g.gameId]?.checked);
  const parseCents     = (str) => {
    const n = parseFloat(str);
    return Number.isFinite(n) ? Math.round(n * 100) : null;
  };
  const selectedTotal  = selectedGames.reduce((s, g) => {
    const c = parseCents(selection[g.gameId]?.amount);
    return s + (Number.isFinite(c) ? c : 0);
  }, 0);

  const submit = async () => {
    if (selectedGames.length === 0) {
      toast.error('Select at least one game');
      return;
    }
    // Validate every selected amount BEFORE sending anything — a typo on
    // one row shouldn't leave the batch half-recorded.
    for (const g of selectedGames) {
      const cents = parseCents(selection[g.gameId]?.amount);
      if (cents == null || cents < 0) {
        toast.error('Enter a valid amount for every selected game');
        return;
      }
    }
    setSubmitting(true);
    const trimmedNote = note.trim() || undefined;
    let okCount = 0;
    let failCount = 0;
    for (const g of selectedGames) {
      const cents = parseCents(selection[g.gameId]?.amount);
      try {
        // eslint-disable-next-line no-await-in-loop -- sequential on purpose: these are financial writes, not read fan-out.
        await api.post(`/payments/team/${teamId}/finance/cash-payment`, {
          userId, gameId: g.gameId, amountCents: cents, note: trimmedNote,
        });
        okCount++;
      } catch (err) {
        failCount++;
        toast.error(`${fmtGameDate(g.gameDate)}: ${err.response?.data?.error || 'Failed to record'}`);
      }
    }
    setSubmitting(false);

    if (okCount > 0) {
      toast.success(`Recorded ${okCount} cash payment${okCount === 1 ? '' : 's'} for ${name} 💵`);
      onRecorded(); // refresh the page's finance summary
    }
    if (failCount === 0) {
      onClose();
    } else {
      // Some rows failed — keep the modal open and re-fetch so any
      // successfully-recorded games drop off the list and the coach can
      // retry just the ones that failed, instead of losing all context.
      try {
        const { data: d } = await api.get(`/payments/team/${teamId}/finance/player/${userId}`);
        const remaining = d?.outstandingGames || [];
        setGames(remaining);
        setSelection(prev => {
          const next = {};
          for (const g of remaining) {
            next[g.gameId] = prev[g.gameId] || { checked: false, amount: ((g.owedCents || 0) / 100).toFixed(2) };
          }
          return next;
        });
      } catch { /* best effort — modal still usable off stale local state */ }
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)', borderRadius: 14, border: '1px solid var(--border)',
          width: '100%', maxWidth: 640,
          // 2026-07-14 (mobile pass) — was `90vh`. Same class of bug already
          // fixed in the shared GamePage/SchedulePage Modal (see their
          // "May 8 2026 (v2.5.86)" comments): plain vh doesn't shrink when
          // the mobile keyboard opens, so with the Note field focused the
          // footer's "Record payment" button could end up under the
          // keyboard. var(--mobile-h) tracks the actual visible area
          // (falls back to 100dvh on desktop / before the JS observer runs,
          // so desktop is unaffected).
          maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.9)',
          display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
        }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', borderBottom: '1px solid var(--border)',
        }}>
          <div>
            <h2 className="font-bold text-base">💵 Record cash payment</h2>
            <p className="text-xs" style={{ color: 'var(--text-3)' }}>{name}</p>
          </div>
          <button
            type="button" onClick={onClose} aria-label="Close"
            style={{
              background: 'transparent', border: '1px solid var(--border)', borderRadius: 8,
              padding: '6px 10px', cursor: 'pointer', color: 'var(--text-2)', fontSize: 14,
            }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ overflow: 'auto', padding: '14px 18px' }}>
          {loading && <p className="text-sm text-secondary py-6 text-center">Loading outstanding games…</p>}
          {!loading && games.length === 0 && (
            <p className="text-sm text-secondary py-6 text-center">
              No outstanding games for this player right now.
            </p>
          )}
          {!loading && games.length > 0 && (
            <>
              <p className="text-xs text-secondary mb-3" style={{ lineHeight: 1.5 }}>
                Pick which game(s) {name} paid for in cash. Amount defaults to the fee for that game
                (goalie rate applied automatically where it applies) — adjust if they paid a different amount.
              </p>
              {games.map(g => {
                const sel = selection[g.gameId] || { checked: false, amount: '0.00' };
                return (
                  <label
                    key={g.gameId}
                    className="card"
                    style={{
                      marginBottom: 8, padding: '10px 12px', background: 'var(--surface-2)',
                      display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                    }}>
                    <input
                      type="checkbox"
                      checked={!!sel.checked}
                      onChange={() => toggle(g.gameId)}
                      style={{ width: 18, height: 18, flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="font-semibold text-sm">{fmtGameDate(g.gameDate)}</div>
                      <div className="text-xs" style={{ color: 'var(--text-3)' }}>
                        {fmtTime12(g.gameTime)}{g.gameTime && g.arena ? ' · ' : ''}{g.arena || ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                      <span className="text-xs" style={{ color: 'var(--text-3)' }}>{(currency || 'cad').toUpperCase()}</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="input-field py-1"
                        style={{ width: 80, textAlign: 'right' }}
                        value={sel.amount}
                        disabled={!sel.checked}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setAmount(g.gameId, e.target.value)}
                      />
                    </div>
                  </label>
                );
              })}

              <div style={{ marginTop: 12 }}>
                <label className="label">Note (optional)</label>
                <input
                  type="text"
                  maxLength={500}
                  className="input-field"
                  placeholder="e.g. paid at the rink July 10"
                  value={note}
                  onChange={(e) => setNoteText(e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!loading && games.length > 0 && (
          <div style={{
            padding: '12px 18px', borderTop: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          }}>
            <div className="text-sm">
              <span style={{ color: 'var(--text-3)' }}>Total: </span>
              <span className="font-bold">{fmtMoney(selectedTotal, currency)}</span>
              {selectedGames.length > 0 && (
                <span className="text-xs" style={{ color: 'var(--text-3)' }}> ({selectedGames.length} game{selectedGames.length === 1 ? '' : 's'})</span>
              )}
            </div>
            <button
              type="button"
              onClick={submit}
              disabled={submitting || selectedGames.length === 0}
              className="btn-ice"
              style={{ opacity: (submitting || selectedGames.length === 0) ? 0.6 : 1 }}>
              {submitting ? 'Recording…' : '💵 Record payment'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// OutstandingPlayerModal — opens from the Outstanding tab. Shows the
// specific games a spare still owes for (date + arena + amount), when a
// payment link was last emailed for each, and a button to send a fresh
// link per game. Uses the same /finance/player/:userId payload, which
// now includes `outstandingGames` (with lastLinkSentAt).
// ─────────────────────────────────────────────────────────────
function OutstandingPlayerModal({ user, data, loading, currency, teamId, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Local copy so a successful send can update "last sent" without a refetch.
  const [games, setGames] = useState([]);
  const [sendingId, setSendingId] = useState(null);
  useEffect(() => { setGames(data?.outstandingGames || []); }, [data]);

  const fmtGameDate = (v) => {
    if (!v) return '—';
    try { return format(parseISO(String(v).slice(0, 10)), 'EEE MMM d, yyyy'); } catch { return '—'; }
  };
  const fmtSent = (v) => {
    if (!v) return null;
    try { return format(parseISO(v), 'MMM d, yyyy h:mm a'); } catch { return null; }
  };

  const totalOwed = games.reduce((s, g) => s + (g.owedCents || 0), 0);

  const sendLink = async (g) => {
    setSendingId(g.gameId);
    try {
      const { data: r } = await api.post(
        `/teams/${teamId}/games/${g.gameId}/availability/${user.userId}/resend-payment-link`
      );
      const amt = r?.amountCents
        ? ` ($${(r.amountCents / 100).toFixed(2)} ${String(r.paymentCurrency || 'CAD').toUpperCase()})`
        : '';
      toast.success(`Payment link sent to ${data?.name || user.name}${amt} 💳`);
      setGames(prev => prev.map(x => x.gameId === g.gameId
        ? { ...x, lastLinkSentAt: new Date().toISOString() } : x));
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || 'Failed to send payment link');
    }
    setSendingId(null);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)', borderRadius: 14, border: '1px solid var(--border)',
          width: '100%', maxWidth: 640, maxHeight: '90vh',
          display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
        }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', borderBottom: '1px solid var(--border)',
        }}>
          <div>
            <h2 className="font-bold text-base">{data?.name || user.name}</h2>
            {data?.email && <p className="text-xs" style={{ color: 'var(--text-3)' }}>{data.email}</p>}
          </div>
          <button
            type="button" onClick={onClose} aria-label="Close"
            style={{
              background: 'transparent', border: '1px solid var(--border)', borderRadius: 8,
              padding: '6px 10px', cursor: 'pointer', color: 'var(--text-2)', fontSize: 14,
            }}>✕</button>
        </div>

        {/* Total owed */}
        {!loading && (
          <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border)' }}>
            <div className="text-xs text-secondary">Total outstanding</div>
            <div className="font-bold" style={{ color: 'var(--penalty)', fontSize: 18 }}>
              {fmtMoney(totalOwed, currency)}
            </div>
          </div>
        )}

        {/* Body — one card per outstanding game */}
        <div style={{ overflow: 'auto', padding: '12px 18px 18px' }}>
          {loading && <p className="text-sm text-secondary py-6 text-center">Loading…</p>}
          {!loading && games.length === 0 && (
            <p className="text-sm text-secondary py-6 text-center">No outstanding games for this player.</p>
          )}
          {!loading && games.map(g => {
            const sentStr = fmtSent(g.lastLinkSentAt);
            return (
              <div key={g.gameId} className="card" style={{ marginBottom: 10, padding: '12px 14px', background: 'var(--surface-2)' }}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-semibold text-sm">{fmtGameDate(g.gameDate)}</div>
                    <div className="text-xs" style={{ color: 'var(--text-3)' }}>
                      {fmtTime12(g.gameTime)}{g.gameTime && g.arena ? ' · ' : ''}{g.arena || ''}
                    </div>
                  </div>
                  <div className="font-bold" style={{ color: 'var(--penalty)' }}>
                    {fmtMoney(g.owedCents, currency)}
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3 flex-wrap" style={{ marginTop: 10 }}>
                  <div className="text-xs" style={{ color: sentStr ? 'var(--text-2)' : 'var(--text-3)' }}>
                    {sentStr ? `Payment link last sent ${sentStr}` : 'No payment link sent yet'}
                  </div>
                  <button
                    type="button"
                    onClick={() => sendLink(g)}
                    disabled={sendingId === g.gameId}
                    style={{
                      padding: '8px 14px', borderRadius: 8,
                      cursor: sendingId === g.gameId ? 'not-allowed' : 'pointer',
                      background: 'rgba(0,212,255,0.12)', border: '1px solid var(--ice-a30, rgba(0,212,255,0.3))',
                      color: 'var(--ice)', fontSize: 13, fontWeight: 700,
                    }}>
                    {sendingId === g.gameId ? 'Sending…' : (sentStr ? '💳 Resend link' : '💳 Send payment link')}
                  </button>
                </div>
              </div>
            );
          })}
          {(() => {
            const st = data?.stripe;
            const mono = { fontFamily: 'var(--font-mono)' };
            // Connected + can charge -> tell the coach where the money lands.
            if (st && st.accountId && st.chargesEnabled) {
              return (
                <div className="mt-2 p-2 rounded" style={{
                  fontSize: 12, lineHeight: 1.5, color: 'var(--text-2)',
                  background: 'rgba(0,232,122,0.06)', border: '1px solid rgba(0,232,122,0.2)',
                }}>
                  💳 Payments are deposited to your Stripe Connect account{' '}
                  <span style={{ ...mono, color: 'var(--goal)' }}>{st.accountId}</span>. Sending a link
                  emails the player a Pay Now button (and an in-app reminder).
                </div>
              );
            }
            // Connected but not charges-enabled -> link send will be blocked.
            if (st && st.accountId && !st.chargesEnabled) {
              return (
                <div className="mt-2 p-2 rounded" style={{
                  fontSize: 12, lineHeight: 1.5, color: 'var(--text-2)',
                  background: 'rgba(255,149,0,0.06)', border: '1px solid rgba(255,149,0,0.2)',
                }}>
                  ⚠ Stripe Connect account <span style={{ ...mono, color: 'var(--puck)' }}>{st.accountId}</span>{' '}
                  is connected but not charges-enabled yet — links won't collect until onboarding is finished
                  (Team Settings → Payments). Until then, collect offline.
                </div>
              );
            }
            // No Stripe account connected.
            return (
              <p className="text-xs text-secondary mt-2" style={{ lineHeight: 1.5 }}>
                No Stripe Connect account is connected, so a payment link can't collect yet — connect Stripe
                in Team Settings → Payments, or collect offline.
              </p>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PlayerTransactionsModal — full transaction history for a single
// player. v2.5.153.
// ─────────────────────────────────────────────────────────────
function PlayerTransactionsModal({ user, data, loading, currency, onClose }) {
  // Close on ESC.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const statusColor = (s) => {
    if (s === 'succeeded' || s === 'paid')          return 'var(--goal)';
    if (s === 'refunded' || s === 'partial_refund') return 'var(--puck)';
    if (s === 'failed' || s === 'canceled')         return 'var(--penalty)';
    return 'var(--text-2)';
  };
  const statusLabel = (s) => {
    if (s === 'succeeded') return 'Paid';
    if (s === 'paid')      return 'Paid';
    if (s === 'refunded')  return 'Refunded';
    if (s === 'partial_refund') return 'Partial refund';
    if (s === 'pending')   return 'Pending';
    if (s === 'failed')    return 'Failed';
    if (s === 'canceled')  return 'Cancelled';
    return s || '—';
  };

  const payments = data?.payments || [];
  const totals   = data?.totals   || { paidCents: 0, refundedCents: 0 };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)', borderRadius: 14,
          border: '1px solid var(--border)',
          width: '100%', maxWidth: 720, maxHeight: '90vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
        }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', borderBottom: '1px solid var(--border)',
        }}>
          <div>
            <h2 className="font-bold text-base">{data?.name || user.name}</h2>
            {data?.email && <p className="text-xs" style={{ color: 'var(--text-3)' }}>{data.email}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent', border: '1px solid var(--border)',
              borderRadius: 8, padding: '6px 10px', cursor: 'pointer',
              color: 'var(--text-2)', fontSize: 14,
            }}>✕</button>
        </div>

        {/* Totals row */}
        {!loading && (
          <div style={{
            display: 'flex', gap: 12, padding: '12px 18px',
            borderBottom: '1px solid var(--border)', flexWrap: 'wrap',
          }}>
            <div>
              <div className="text-xs text-secondary">Total paid</div>
              <div className="font-bold" style={{ color: 'var(--goal)', fontSize: 16 }}>
                {fmtMoney(totals.paidCents, currency)}
              </div>
            </div>
            <div>
              <div className="text-xs text-secondary">Refunded</div>
              <div className="font-bold" style={{ color: 'var(--puck)', fontSize: 16 }}>
                {totals.refundedCents > 0 ? fmtMoney(totals.refundedCents, currency) : '—'}
              </div>
            </div>
            <div>
              <div className="text-xs text-secondary">Transactions</div>
              <div className="font-bold" style={{ fontSize: 16 }}>{payments.length}</div>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <ExportMenu
                filename={`payments-${(data?.name || 'player').replace(/\s+/g, '-')}`}
                title={`Transactions — ${data?.name || user.name}`}
                columns={[
                  { key: 'createdAt',  label: 'Date' },
                  { key: 'game',       label: 'Game' },
                  { key: 'arena',      label: 'Arena' },
                  { key: 'amount',     label: 'Amount',   align: 'right' },
                  { key: 'refunded',   label: 'Refunded', align: 'right' },
                  { key: 'status',     label: 'Status' },
                  { key: 'type',       label: 'Type' },
                ]}
                getRows={() => payments.map(p => {
                  let dateStr = '';
                  try { if (p.createdAt) dateStr = format(parseISO(p.createdAt), 'yyyy-MM-dd HH:mm'); } catch {}
                  let gameStr = '';
                  try { if (p.gameDate) gameStr = format(parseISO(String(p.gameDate).slice(0, 10)), 'MMM d, yyyy'); } catch {}
                  return {
                    createdAt: dateStr,
                    game:      gameStr,
                    arena:     p.arena || '',
                    amount:    fmtMoney(p.amountCents, p.currency || currency),
                    refunded:  p.refundedCents > 0 ? fmtMoney(p.refundedCents, p.currency || currency) : '',
                    status:    statusLabel(p.status),
                    type:      p.paymentType || '',
                  };
                })}
              />
            </div>
          </div>
        )}

        {/* Body */}
        <div style={{ overflow: 'auto', padding: '0 18px 18px' }}>
          {loading && <p className="text-sm text-secondary py-6 text-center">Loading transactions…</p>}
          {!loading && payments.length === 0 && (
            <p className="text-sm text-secondary py-6 text-center">No transactions yet.</p>
          )}
          {!loading && payments.length > 0 && (
            <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th className="text-left py-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}>Date</th>
                  <th className="text-left py-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}>Game</th>
                  <th className="text-right py-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}>Amount</th>
                  <th className="text-left py-2 text-xs font-bold" style={{ color: 'var(--text-2)' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {payments.map(p => {
                  let createdStr = '—';
                  try { if (p.createdAt) createdStr = format(parseISO(p.createdAt), 'MMM d, yyyy h:mm a'); } catch {}
                  let gameStr = p.gameId ? '—' : '(non-game)';
                  try { if (p.gameDate) gameStr = format(parseISO(String(p.gameDate).slice(0, 10)), 'MMM d'); } catch {}
                  return (
                    <tr key={p.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td className="py-2 pr-2 text-xs" style={{ color: 'var(--text-2)' }}>{createdStr}</td>
                      <td className="py-2 pr-2">
                        <div>{gameStr}</div>
                        {p.arena && <div className="text-xs" style={{ color: 'var(--text-3)' }}>{p.arena}</div>}
                      </td>
                      <td className="py-2 pr-2 text-right">
                        <div style={{ color: (p.status === 'succeeded' || p.status === 'paid') ? 'var(--goal)' : 'var(--text)' }}>
                          {fmtMoney(p.amountCents, p.currency || currency)}
                        </div>
                        {p.refundedCents > 0 && (
                          <div className="text-xs" style={{ color: 'var(--puck)' }}>
                            −{fmtMoney(p.refundedCents, p.currency || currency)} refunded
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-2 text-xs" style={{ color: statusColor(p.status), fontWeight: 600 }}>
                        {statusLabel(p.status)}
                        {p.stripeMode === 'test' && (
                          <span className="ml-1 text-xs" style={{ color: 'var(--text-3)', fontWeight: 400 }}>(test)</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Tiny presentational helpers — kept inline so the page is one
//    self-contained file. These are small enough that extracting
//    would just add ceremony.
function Tile({ label, value, sub, tone }) {
  const toneStyles = {
    goal:    { color: 'var(--goal)',    bg: 'rgba(0,232,122,0.06)',  border: 'rgba(0,232,122,0.2)' },
    puck:    { color: 'var(--puck)',    bg: 'rgba(255,149,0,0.06)',  border: 'rgba(255,149,0,0.2)' },
    penalty: { color: 'var(--penalty)', bg: 'rgba(255,68,68,0.06)',  border: 'rgba(255,68,68,0.2)' },
    muted:   { color: 'var(--text-2)',  bg: 'var(--surface-2)',      border: 'var(--border)' },
  };
  const s = toneStyles[tone] || toneStyles.muted;
  return (
    <div className="card" style={{
      background: s.bg, border: `1px solid ${s.border}`, padding: '12px 14px',
    }}>
      <div className="text-xs font-semibold uppercase tracking-wider mb-1"
        style={{ color: 'var(--text-2)', letterSpacing: '0.06em' }}>
        {label}
      </div>
      <div className="font-bold" style={{ fontSize: 18, color: s.color, fontFamily: 'var(--font-display)', wordBreak: 'break-word' }}>
        {value}
      </div>
      {sub && (
        <div className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-3)', letterSpacing: '0.06em', fontWeight: 600 }}>
        {k}
      </div>
      <div className="font-semibold mt-0.5">{v}</div>
    </div>
  );
}
